import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useAuth } from '../context/AuthContext';
import { useGuestGate } from '../context/GuestGateContext';
import { useKyc, kycLinkFromError } from '../context/KycContext';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, SummaryRow, PayButton, Dropdown, TextField,
} from '../components/Form';
import AmountKeypad from '../components/AmountKeypad';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';

interface PaymentMethod { id: string; name: string; gateway?: string | null; }
interface Game { id: string; name: string; credentialRequestsEnabled?: boolean; }
interface WithdrawLimits { min: number; max: number; tipPercent?: number; }

// Mirrors the backend MIN_CARD_WITHDRAW_AMOUNT floor on wallet cash-outs.
const WALLET_MIN_WITHDRAW = 30;

// The house tip withheld from a bank payout. Mirrors resolveWithdrawTip() on the
// server (backend/src/orders/withdraw-tip.ts) — same FLOOR, so the slip shown
// here is exactly what the order is stamped with. min/max are checked against
// the amount asked for, pre-tip.
function tipFor(amount: number, percent?: number) {
  const pct = Number(percent) || 0;
  const amt = Number(amount) || 0;
  if (pct <= 0 || amt <= 0) return { tip: 0, receive: amt };
  const tip = Math.floor((amt * pct) / 100);
  return { tip, receive: amt - tip };
}

/**
 * The payout slip: what you asked for, what the service fee takes, what lands in
 * your account. Shown before submitting so the amount is never a surprise.
 * Renders nothing until there's an amount and a fee to show.
 */
function TipSlip({ amount, percent }: { amount: number; percent?: number }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { tip, receive } = tipFor(amount, percent);
  if (!amount || tip <= 0) return null;
  return (
    <div style={{
      marginTop: 14, padding: '8px 12px',
      backgroundColor: colors.surfaceMuted, borderRadius: 12,
      border: `1px solid ${colors.border}`,
    }}>
      <SummaryRow label={t('withdraw.slip.requested')} value={`$${amount.toFixed(2)}`} />
      <SummaryRow label={t('withdraw.slip.fee', { percent })} value={`-$${tip.toFixed(2)}`} />
      <div style={{ height: 1, backgroundColor: colors.border, margin: '4px 0' }} />
      <SummaryRow label={t('withdraw.slip.receive')} value={<span style={{ color: colors.accentText }}>${receive.toFixed(2)}</span>} />
    </div>
  );
}
interface WalletSummary { balance: number; }

type WithdrawMode = 'choice' | 'redemption' | 'gameBank' | 'wallet';

// TheSolar pays the player back to the card they deposited with, so no payout
// tag is collected for card-gateway methods — we store this sentinel instead.
const CARD_PAYOUT_TAG = 'Card payout (TheSolar)';

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

export default function Withdraw() {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const { alert } = useAlert();
  const { t } = useTranslation();
  const { isGuest } = useAuth();
  const { requireAuth } = useGuestGate();
  const { mustVerify, openKyc, refresh: refreshKyc } = useKyc();

  const initialMode = (() => {
    const m = new URLSearchParams(location.search).get('mode');
    return m === 'wallet' || m === 'redemption' || m === 'gameBank' ? (m as WithdrawMode) : 'choice';
  })();

  const [mode, setMode] = useState<WithdrawMode>(initialMode);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [limits, setLimits] = useState<WithdrawLimits>({ min: 40, max: 2000 });
  const [walletBalance, setWalletBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [success, setSuccess] = useState<{ amount: number; destination: string; redemption: boolean } | null>(null);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Live wallet balance — the reported bug was that this only fetched once on
  // mount and went stale. Refresh on mount, on order updates (socket), and when
  // the tab/window regains focus so the balance matches the backend.
  const refreshWallet = useCallback(async () => {
    if (isGuest) return; // no wallet to read while browsing
    try {
      const res = await api.get<WalletSummary>('/wallet/me');
      setWalletBalance(Number(res.data.balance || 0));
    } catch { /* best-effort */ }
  }, [isGuest]);

  useEffect(() => {
    (async () => {
      try {
        const [methodsRes, gamesRes, limitsRes, walletRes] = await Promise.allSettled([
          api.get<PaymentMethod[]>('/payment-methods'),
          api.get<Game[]>('/games'),
          api.get<WithdrawLimits>('/orders/withdraw/my-limits'),
          api.get<WalletSummary>('/wallet/me'),
        ]);
        if (methodsRes.status === 'fulfilled') {
          setPaymentMethods(methodsRes.value.data);
          if (methodsRes.value.data.length > 0) setSelectedMethod(methodsRes.value.data[0]);
        }
        if (gamesRes.status === 'fulfilled') {
          setGames(gamesRes.value.data.filter(allowsCredentialRequests));
        }
        if (limitsRes.status === 'fulfilled') setLimits(limitsRes.value.data);
        if (walletRes.status === 'fulfilled') setWalletBalance(Number(walletRes.value.data.balance || 0));
        if (methodsRes.status === 'rejected' || gamesRes.status === 'rejected') {
          alert(t('withdraw.alerts.loadWarningTitle'), t('withdraw.alerts.loadWarningMessage'));
        }
      } catch {
        alert(t('withdraw.alerts.errorTitle'), t('withdraw.alerts.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [alert, t]);

  // Re-fetch wallet on order updates + when the tab regains focus/visibility.
  useEffect(() => {
    refreshWallet();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshWallet(); };
    const onFocus = () => refreshWallet();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshWallet, orderUpdateCount]);

  // Withdrawal limits are per-game: refetch with the selected game's id whenever
  // it changes so the displayed range matches what the server enforces at submit
  // (the deposit that sets the limit — and any happy-hour bonus on it — is scoped
  // to that game).
  useEffect(() => {
    if (!selectedGame) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<WithdrawLimits>('/orders/withdraw/my-limits', { params: { gameId: selectedGame.id } });
        if (!cancelled) setLimits(res.data);
      } catch { /* keep previously loaded limits */ }
    })();
    return () => { cancelled = true; };
  }, [selectedGame]);

  const resetForm = (nextMode: WithdrawMode) => {
    setAmount('');
    setTag('');
    if (nextMode === 'wallet') setSelectedGame(null);
    setMode(nextMode);
    setShowReviewModal(false);
  };

  const isThesolarMethod = selectedMethod?.gateway === 'thesolar';

  // External cash-outs (wallet and game→bank) always pay out through Solar
  // (card and CashApp follow the same rail — Solar pays the player's saved
  // payout method), so the method picker is hidden and the Solar method is
  // auto-selected.
  const solarMethod = paymentMethods.find(m => m.gateway === 'thesolar') || null;
  useEffect(() => {
    if ((mode === 'wallet' || mode === 'gameBank') && solarMethod && selectedMethod?.id !== solarMethod.id) {
      setSelectedMethod(solarMethod);
    }
  }, [mode, solarMethod, selectedMethod]);

  const getValidatedAmount = () => {
    const parsed = Number(amount);
    if (!amount || Number.isNaN(parsed) || parsed <= 0) {
      alert(t('withdraw.alerts.invalidAmountTitle'), t('withdraw.alerts.invalidAmount'));
      return null;
    }
    if (!/^\d+$/.test(amount.trim())) {
      alert(t('withdraw.alerts.invalidAmountTitle'), t('withdraw.alerts.decimalsOnly'));
      return null;
    }
    const parsedCents = Math.round(parsed * 100);
    if (mode === 'redemption' || mode === 'gameBank') {
      if (!selectedGame) { alert(t('withdraw.alerts.missingInfoTitle'), t('withdraw.alerts.selectGameToWithdraw')); return null; }
      if (parsedCents < Math.round(limits.min * 100)) { alert(t('withdraw.alerts.amountTooLowTitle'), t('withdraw.alerts.minGameWithdrawal', { min: limits.min })); return null; }
      if (parsedCents > Math.round(limits.max * 100)) { alert(t('withdraw.alerts.amountTooHighTitle'), t('withdraw.alerts.maxGameWithdrawal', { max: limits.max })); return null; }
    }
    if (mode === 'gameBank' || mode === 'wallet') {
      if (!selectedMethod) { alert(t('withdraw.alerts.missingInfoTitle'), t('withdraw.alerts.selectReceivingMethod')); return null; }
      if (!isThesolarMethod && !tag.trim()) { alert(t('withdraw.alerts.missingInfoTitle'), t('withdraw.alerts.enterPayoutAccount')); return null; }
    }
    if (mode === 'wallet' && parsedCents < WALLET_MIN_WITHDRAW * 100) {
      alert(t('withdraw.alerts.amountTooLowTitle'), t('withdraw.alerts.minWalletWithdrawal', { min: WALLET_MIN_WITHDRAW }));
      return null;
    }
    if (mode === 'wallet' && parsedCents > Math.round(walletBalance * 100)) {
      alert(t('withdraw.alerts.insufficientBalanceTitle'), t('withdraw.alerts.walletAvailable', { balance: walletBalance.toFixed(2) }));
      return null;
    }
    return parsed;
  };

  const handleReview = () => {
    // KYC is required before any withdrawal — open the verification form instead
    // of proceeding if the player hasn't verified yet.
    if (mustVerify) { openKyc(); return; }
    if (getValidatedAmount() == null) return;
    setShowReviewModal(true);
  };

  const handleSubmit = async () => {
    const parsedAmount = getValidatedAmount();
    if (!parsedAmount) return;
    setShowReviewModal(false);
    setSubmitting(true);
    try {
      if (mode === 'redemption') {
        if (!selectedGame) return;
        await api.post('/orders/redemption/request', { gameId: selectedGame.id, amount: parsedAmount });
      } else if (mode === 'gameBank') {
        if (!selectedGame || !selectedMethod) return;
        await api.post('/orders/withdraw', {
          gameId: selectedGame.id,
          paymentMethodId: selectedMethod.id,
          payoutTag: isThesolarMethod ? CARD_PAYOUT_TAG : tag.trim(),
          amount: parsedAmount,
        });
      } else {
        if (!selectedMethod) return;
        await api.post('/orders/withdraw/wallet', {
          paymentMethodId: selectedMethod.id,
          payoutTag: isThesolarMethod ? CARD_PAYOUT_TAG : tag.trim(),
          amount: parsedAmount,
        });
        setWalletBalance(b => Math.max(0, Math.round(b * 100 - parsedAmount * 100) / 100));
      }
      const destination = mode === 'redemption'
        ? t('withdraw.review.slBalance')
        : isThesolarMethod
          ? t('withdraw.cardPayout.reviewAccount')
          : tag.trim();
      setSuccess({ amount: parsedAmount, destination, redemption: mode === 'redemption' });
      setAmount('');
      setTag('');
    } catch (err: any) {
      // Inquiry-flow KYC surfaced at submit (status was stale). Refresh so the
      // link + red dot populate; the "Verify your identity" prompt opens the
      // hosted page within a click gesture (popups can't open post-await here).
      if (kycLinkFromError(err) !== null) {
        await refreshKyc();
        alert(t('withdraw.alerts.errorTitle'), t('kyc.intro', 'We need to confirm your identity before you can withdraw.'));
        return;
      }
      const message = err?.response?.data?.message || t('withdraw.alerts.submitFailed');
      if (mode === 'wallet' && /below minimum|exceeds maximum/i.test(message)) {
        alert(t('withdraw.alerts.modeErrorTitle'), t('withdraw.alerts.modeErrorMessage'));
      } else {
        alert(t('withdraw.alerts.errorTitle'), message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isRedemption = mode === 'redemption';
  const isGameBank = mode === 'gameBank';
  const isGameSource = isRedemption || isGameBank;
  const reviewTitle = isRedemption ? t('withdraw.reviewTitle.redemption') : t('withdraw.reviewTitle.withdrawal');
  const requestType = isRedemption ? t('withdraw.requestType.redemption') : isGameBank ? t('withdraw.requestType.gameBank') : t('withdraw.requestType.wallet');
  const reviewSource = isGameSource
    ? t('withdraw.review.gameBalance', { game: selectedGame?.name || t('withdraw.selectAGame') })
    : t('withdraw.review.slBalance');
  const reviewDestination = isRedemption
    ? t('withdraw.review.slBalance')
    : isThesolarMethod
      ? t('withdraw.cardPayout.reviewAccount')
      : tag.trim();
  const reviewAmount = Number(amount || 0);
  const reviewTip = mode === 'redemption' ? 0 : tipFor(reviewAmount, limits.tipPercent).tip;
  const dynamicSubmitLabel = isRedemption
    ? t('withdraw.submitLabel.moveAmount', { amount: reviewAmount.toFixed(2) })
    : t('withdraw.submitLabel.cashOutAmount', { amount: reviewAmount.toFixed(2) });

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader
        icon="arrow-circle-down"
        title={t('withdraw.title')}
        right={<Icon name="shield-alt" size={16} color={colors.accentDark} />}
      />

      {success ? (
        <div style={{ padding: 20, paddingTop: 48, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, margin: '0 auto 18px', borderRadius: 32, background: colors.accentSoft, display: 'grid', placeItems: 'center' }}>
            <Icon name="check" size={26} color={colors.accent} />
          </div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: colors.text }}>{t('withdraw.success.title')}</h1>
          <p style={{ margin: '10px auto 8px', maxWidth: 360, fontSize: 17, lineHeight: 1.5, color: colors.text }}>
            {success.redemption
              ? t('withdraw.success.redemption', { amount: success.amount.toFixed(2) })
              : t('withdraw.success.withdrawal', { amount: success.amount.toFixed(2), destination: success.destination })}
          </p>
          <p style={{ margin: '0 auto 24px', maxWidth: 360, fontSize: 15, lineHeight: 1.5, color: colors.textMuted }}>{t('withdraw.success.timing')}</p>
          <Button label={t('withdraw.success.track')} onClick={() => navigate('/transactions')} />
          <Button label={t('withdraw.success.done')} variant="secondary" onClick={() => { setSuccess(null); resetForm('choice'); }} style={{ marginTop: 10 }} />
        </div>
      ) : loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={4} /></div>
      ) : (
        <div style={{ padding: 20, paddingBottom: 40 }}>
          {mode === 'choice' ? (
            <div data-tour="withdraw-choice">
              <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: colors.text }}>{t('withdraw.whereIsMoney')}</h2>

              <PathCard
                icon="gamepad"
                title={t('withdraw.paths.gameLocation.title')}
                body={t('withdraw.paths.gameLocation.body')}
                actionLabel={t('withdraw.paths.gameLocation.action')}
                onClick={() => resetForm('redemption')}
              />
              <PathCard
                icon="credit-card"
                title={t('withdraw.paths.balanceLocation.title')}
                body={t('withdraw.paths.balanceLocation.body', { balance: walletBalance.toFixed(2) })}
                actionLabel={t('withdraw.paths.balanceLocation.action')}
                onClick={() => resetForm('wallet')}
              />
              <button onClick={() => setShowOtherOptions(open => !open)} aria-expanded={showOtherOptions} style={{ width: '100%', minHeight: 52, marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: 0, background: 'transparent', color: colors.text, fontSize: 16, fontWeight: 600, textAlign: 'left' }}>
                {t('withdraw.otherOptions')}
                <Icon name={showOtherOptions ? 'chevron-up' : 'chevron-down'} size={14} color={colors.textMuted} />
              </button>
              {showOtherOptions ? (
                <PathCard icon="building-columns" title={t('withdraw.paths.gameBank.title')} body={t('withdraw.paths.gameBank.body')} actionLabel={t('withdraw.paths.gameBank.action')} onClick={() => resetForm('gameBank')} />
              ) : null}
            </div>
          ) : (
            <>
              <button
                onClick={() => resetForm('choice')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: colors.textSub, fontSize: 14, fontWeight: 800,
                  marginBottom: 4, minHeight: 44,
                }}
              >
                <Icon name="arrow-left" size={13} color={colors.textSub} />
                {t('withdraw.changeType')}
              </button>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 16, borderRadius: 14,
                backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 14,
                  backgroundColor: isGameSource ? colors.infoSoft : colors.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={isGameSource ? 'gamepad' : 'wallet'} size={13} color={isGameSource ? colors.infoText : colors.accentText} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.textSub, letterSpacing: 0.3 }}>
                    {isRedemption ? t('withdraw.modeLabel.redemption') : isGameBank ? t('withdraw.modeLabel.gameBank') : t('withdraw.modeLabel.wallet')}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: colors.text, marginTop: 2 }}>
                    {isGameSource ? t('withdraw.limitsRange', { min: limits.min, max: limits.max }) : t('withdraw.availableWallet', { balance: walletBalance.toFixed(2) })}
                  </div>
                  <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 4 }}>
                    {isGameSource ? t('withdraw.limitsNote.game') : t('withdraw.limitsNote.wallet')}
                  </div>
                </div>
              </div>

              {isGameSource ? (
                <>
                  <SectionLabel>{t('withdraw.steps.withdrawFromGame')}</SectionLabel>
                  <Dropdown icon="gamepad" text={selectedGame?.name || ''} placeholder={t('withdraw.selectAGame')} onClick={() => setShowGamePicker(true)} />

                  {isGameBank ? (
                    <>
                      {solarMethod ? (
                        // Game→external also rides the Solar rail — no method to pick.
                        <CardPayoutNote method={solarMethod.name} />
                      ) : (
                        <>
                          <SectionLabel>{t('withdraw.steps.receivingMethodGame')}</SectionLabel>
                          <MethodGrid methods={paymentMethods} selected={selectedMethod} onSelect={setSelectedMethod} />
                          {isThesolarMethod ? (
                            <CardPayoutNote method={selectedMethod?.name} />
                          ) : (
                            <>
                              <SectionLabel>{t('withdraw.steps.accountGame', { method: selectedMethod?.name || t('withdraw.receivingDefault') })}</SectionLabel>
                              <TextField
                                icon="tag"
                                value={tag}
                                onChange={setTag}
                                placeholder={t('withdraw.accountPlaceholder', { method: selectedMethod?.name || t('withdraw.payoutDefault') })}
                                autoCapitalize="none"
                              />
                            </>
                          )}
                        </>
                      )}

                      <SectionLabel>{t('withdraw.steps.amountGameBank')}</SectionLabel>
                    </>
                  ) : (
                    <SectionLabel>{t('withdraw.steps.amountRedemption')}</SectionLabel>
                  )}
                  <div style={{ paddingTop: 6 }}>
                    <AmountKeypad
                      value={amount}
                      onChange={setAmount}
                      max={limits.max}
                      allowDecimal={false}
                      hint={t('withdraw.minAmount', { min: limits.min })}
                    />
                  </div>
                  {/* Payout slip: what they asked for, what we keep, what lands.
                      isGameBank, NOT isGameSource — a game→wallet redemption is an
                      internal transfer and is never tipped. */}
                  {isGameBank && <TipSlip amount={Number(amount)} percent={limits.tipPercent} />}
                </>
              ) : (
                <>
                  {solarMethod ? (
                    // All wallet cash-outs ride the Solar rail — no method to pick.
                    <CardPayoutNote method={solarMethod.name} />
                  ) : (
                    <>
                      <SectionLabel>{t('withdraw.steps.receivingMethodWallet')}</SectionLabel>
                      <MethodGrid methods={paymentMethods} selected={selectedMethod} onSelect={setSelectedMethod} />
                      {isThesolarMethod ? (
                        <CardPayoutNote method={selectedMethod?.name} />
                      ) : (
                        <>
                          <SectionLabel>{t('withdraw.steps.accountWallet', { method: selectedMethod?.name || t('withdraw.receivingDefault') })}</SectionLabel>
                          <TextField
                            icon="tag"
                            value={tag}
                            onChange={setTag}
                            placeholder={t('withdraw.accountPlaceholder', { method: selectedMethod?.name || t('withdraw.payoutDefault') })}
                            autoCapitalize="none"
                          />
                        </>
                      )}
                    </>
                  )}

                  <SectionLabel>{t('withdraw.steps.amountWallet')}</SectionLabel>
                  <div style={{ paddingTop: 6 }}>
                    <AmountKeypad
                      value={amount}
                      onChange={setAmount}
                      max={Math.floor(walletBalance)}
                      allowDecimal={false}
                      hint={t('withdraw.maxAmount', { balance: walletBalance.toFixed(2) })}
                    />
                  </div>
                  {/* Wallet→bank is money leaving too, so it carries the same tip. */}
                  <TipSlip amount={Number(amount)} percent={limits.tipPercent} />

                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    backgroundColor: colors.warningSoft, border: `1px solid ${colors.warning}`,
                    borderRadius: 12, padding: 12, marginTop: 18,
                  }}>
                    <Icon name="exclamation-triangle" size={13} color={colors.warningText} style={{ marginTop: 2 }} />
                    <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5, fontWeight: 600, color: colors.warningText }}>
                      {t('withdraw.walletDebitNote')}
                    </div>
                  </div>
                </>
              )}

              <PayButton label={reviewTitle} onClick={() => requireAuth(handleReview)} loading={submitting} />
            </>
          )}
        </div>
      )}

      <Modal open={showReviewModal} onClose={() => setShowReviewModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{reviewTitle}</div>
        <div style={{
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`,
          borderRadius: 14, padding: 14, marginBottom: 16,
        }}>
          <SummaryRow label={t('withdraw.review.type')} value={requestType} />
          <SummaryRow label={t('withdraw.review.amount')} value={`$${Number(amount || 0).toFixed(2)}`} />
          <SummaryRow label={t('withdraw.review.source')} value={reviewSource} />
          <SummaryRow label={t('withdraw.review.destination')} value={reviewDestination} />
          {/* Only shown when the house actually withholds something. */}
          {reviewTip > 0 ? <SummaryRow label={t('withdraw.review.fee')} value={`$${reviewTip.toFixed(2)}`} /> : null}
          <SummaryRow label={t('withdraw.review.remaining')} value={mode === 'wallet' ? `$${Math.max(0, walletBalance - reviewAmount).toFixed(2)}` : t('withdraw.review.confirmedByGame')} />
          <SummaryRow label={t('withdraw.review.timing')} value={t('withdraw.review.timingValue')} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label={t('actions.cancel')} variant="outline" onClick={() => setShowReviewModal(false)} />
          <Button label={dynamicSubmitLabel} loading={submitting} onClick={handleSubmit} />
        </div>
      </Modal>

      <Modal open={showGamePicker} onClose={() => setShowGamePicker(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{t('withdraw.selectGame')}</div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {games.map(g => (
            <button
              key={g.id}
              onClick={() => { setSelectedGame(g); setShowGamePicker(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 0', borderBottom: `1px solid ${colors.divider}`,
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                minHeight: 50,
              }}
            >
              <Icon name="gamepad" size={14} color={selectedGame?.id === g.id ? colors.accentDark : colors.textSub} />
              <div style={{
                flex: 1, fontSize: 15,
                color: selectedGame?.id === g.id ? colors.accentDark : colors.text,
                fontWeight: selectedGame?.id === g.id ? 700 : 400,
              }}>
                {g.name}
              </div>
              {selectedGame?.id === g.id && <Icon name="check" size={13} color="#00D632" />}
            </button>
          ))}
        </div>
        <Button label={t('actions.cancel')} variant="outline" onClick={() => setShowGamePicker(false)} style={{ marginTop: 12 }} />
      </Modal>

    </div>
  );
}

function MethodGrid({
  methods, selected, onSelect,
}: {
  methods: PaymentMethod[];
  selected: PaymentMethod | null;
  onSelect: (m: PaymentMethod) => void;
}) {
  const { colors } = useTheme();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {methods.map(m => (
        <Chip
          key={m.id}
          active={selected?.id === m.id}
          onClick={() => onSelect(m)}
          icon={<Icon name="wallet" size={13} color={selected?.id === m.id ? '#000000' : colors.textSub} />}
          label={m.name}
        />
      ))}
    </div>
  );
}

function PathCard({ icon, title, body, actionLabel, onClick }: { icon: string; title: string; body: string; actionLabel: string; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        borderRadius: 14, padding: 14, marginBottom: 12, minHeight: 112,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={18} color={colors.accentText} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: colors.text }}>{title}</div>
        <div style={{ fontSize: 15, lineHeight: 1.4, color: colors.textSub, marginTop: 4 }}>{body}</div>
        <div style={{ marginTop: 10, color: colors.accent, fontSize: 15, fontWeight: 600 }}>{actionLabel}</div>
      </div>
      <Icon name="chevron-right" size={14} color={colors.textMuted} />
    </button>
  );
}

// Shown in place of the payout-tag field when a TheSolar (card) method is
// selected: the payout goes to the player's default saved card/Venmo, managed
// on the Cash-out methods page — so no account/tag is collected here.
function CardPayoutNote({ method }: { method?: string }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginTop: 12,
    }}>
      <Icon name="credit-card" size={14} color={colors.accentText} style={{ marginTop: 2 }} />
      <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5, fontWeight: 600, color: colors.accentText }}>
        {t('withdraw.cardPayout.note', { method: method || t('withdraw.receivingDefault') })}
        <button
          onClick={() => navigate('/payout-methods')}
          style={{
            display: 'block', marginTop: 6, background: 'transparent', border: 'none', padding: 0,
            color: colors.accentText, fontSize: 14, fontWeight: 800, textDecoration: 'underline', cursor: 'pointer',
          }}
        >
          {t('withdraw.cardPayout.manage')}
        </button>
      </div>
    </div>
  );
}
